import { useState } from 'react';

import IndustrialLiveDemoFrame from './IndustrialLiveDemoFrame';

type Translate = (key: string) => string;
type DashboardId = 'motor' | 'click';
type LocaleKey = 'ko' | 'en';

type DashboardImageAsset = {
  src: string;
  width: number;
  height: number;
};

type DashboardItem = {
  id: DashboardId;
  label: string;
  alt: string;
  /**
   * Console this dashboard runs live, without a language parameter. Every
   * origin listed here has to be in the site's `frame-src` directive in
   * `next.config.js`.
   */
  demoUrl: string;
  images: Record<LocaleKey, DashboardImageAsset>;
};

interface Props {
  lang: string;
  t: Translate;
}

/*
 * transition 단축 속성 값에 쉼표가 들어가면 Tailwind arbitrary property 생성이 깨지고,
 * prefers-reduced-motion 해제까지 함께 묶어야 해서 globals.css 의
 * .industrial-dashboard-button 클래스로 남긴다.
 */
const DASHBOARD_BUTTON_BASE_CLASS =
  'industrial-dashboard-button inline-flex min-h-[40px] cursor-pointer touch-manipulation items-center justify-center rounded-[999px] px-[18px] text-[14px] font-bold leading-[140%] focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-apps-corePrimary01';

/*
 * 원본은 base color → [data-active='true'] → hover 미디어의 :hover →
 * [data-active='true']:hover 순으로 색이 겹겹이 덮였다.
 * 유틸리티가 전부 !important 라 같은 속성이 두 번 나오면 승자가 CSS 출력 순서로
 * 정해지므로, 최종 색을 여기서 계산해 속성당 클래스를 하나만 내보낸다.
 * 활성 상태는 hover 시에도 coreGrayBlack 이라(원본 마지막 규칙) hover 클래스가 필요 없다.
 * 비활성 배경은 원본에도 선언이 없어(reset 의 button 투명 배경) bg 유틸리티를 붙이지 않는다.
 */
const DASHBOARD_BUTTON_STATE_CLASS = {
  active: 'bg-apps-coreGrayWhite text-apps-coreGrayBlack',
  inactive:
    'text-apps-brandGray600 hover:[@media(hover:hover)_and_(pointer:fine)]:text-apps-coreGrayWhite',
} as const;

const DASHBOARDS: DashboardItem[] = [
  {
    id: 'click',
    label: '체결 검사 (ListenAI Click)',
    alt: '체결 검사 (ListenAI Click) 대시보드',
    demoUrl: 'https://click-demo.deeplyinc.com/inspection',
    images: {
      ko: {
        src: '/images/apps/solution/industrial/dashboard-click-ko-v2.png',
        width: 2880,
        height: 1800,
      },
      en: {
        src: '/images/apps/solution/industrial/dashboard-click-en-v2.png',
        width: 2880,
        height: 1800,
      },
    },
  },
  {
    id: 'motor',
    label: '모터 이음 검사',
    alt: '모터 이음 검사 대시보드',
    demoUrl: 'https://motor-demo.deeplyinc.com/motor-qc',
    images: {
      ko: {
        src: '/images/apps/solution/industrial/dashboard-motor-ko-v2.png',
        width: 2880,
        height: 1800,
      },
      en: {
        src: '/images/apps/solution/industrial/dashboard-motor-en-v2.png',
        width: 2880,
        height: 1800,
      },
    },
  },
];

/*
 * 콘솔은 교차 출처라 우리 쪽에서 그 안의 언어 설정을 건드릴 수 없다.
 * `lang` 파라미터가 유일한 통로이고, 콘솔은 이 값을 저장된 설정보다 우선한다.
 * (industrial-base console/src/i18n/utils.ts 의 resolveQueryLocale)
 */
function buildDemoUrl(demoUrl: string, locale: LocaleKey): string {
  const url = new URL(demoUrl);
  url.searchParams.set('lang', locale);

  return url.toString();
}

export default function IndustrialDashboardShowcase({ lang, t }: Props) {
  const [activeDashboardId, setActiveDashboardId] =
    useState<DashboardId>('click');
  const localeKey: LocaleKey = lang === 'en' ? 'en' : 'ko';
  const activeDashboard =
    DASHBOARDS.find((dashboard) => dashboard.id === activeDashboardId) ??
    DASHBOARDS[0];
  const activeImage = activeDashboard.images[localeKey];

  return (
    <div className="grid gap-[16px] lg:gap-[20px]">
      <div
        role="group"
        aria-label={t('대시보드 유형')}
        className="inline-grid grid-cols-2 justify-self-stretch gap-[4px] rounded-[999px] border border-[rgba(255,255,255,0.12)] bg-[rgba(255,255,255,0.05)] p-[4px] md:min-w-[340px] md:justify-self-start"
      >
        {DASHBOARDS.map((dashboard) => {
          const isActive = dashboard.id === activeDashboardId;

          return (
            <button
              key={dashboard.id}
              type="button"
              aria-pressed={isActive}
              data-active={isActive ? 'true' : undefined}
              onClick={() => setActiveDashboardId(dashboard.id)}
              className={`${DASHBOARD_BUTTON_BASE_CLASS} ${
                isActive
                  ? DASHBOARD_BUTTON_STATE_CLASS.active
                  : DASHBOARD_BUTTON_STATE_CLASS.inactive
              }`}
            >
              {t(dashboard.label)}
            </button>
          );
        })}
      </div>
      {/*
        key 로 콘솔마다 프레임을 새로 만든다. 탭을 바꾸면 이전 콘솔이 살아 있는
        채로 새 스틸 뒤에 남는 일이 없어야 한다.
      */}
      <IndustrialLiveDemoFrame
        key={`${activeDashboard.id}-${localeKey}`}
        demoUrl={buildDemoUrl(activeDashboard.demoUrl, localeKey)}
        poster={activeImage}
        frameTitle={t(activeDashboard.alt)}
        runLabel={t('라이브 데모 실행')}
        loadingLabel={t('데모 콘솔을 불러오는 중입니다')}
        unavailableLabel={t('데모 콘솔을 불러오지 못했습니다')}
        openLabel={t('새 창에서 열기')}
      />
    </div>
  );
}
